import type { ButtonHTMLAttributes, ReactNode, SVGProps } from 'react'
import clsx from 'clsx'

export function AuthHeader({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="mt-14">
      <h2 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h2>
      {subtitle && <p className="mt-2 text-sm leading-6 text-gray-500">{subtitle}</p>}
    </div>
  )
}

export function AuthDivider({ children }: { children: ReactNode }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-gray-200" />
      </div>
      <div className="relative flex justify-center">
        <span className="bg-white px-4 text-xs font-medium uppercase tracking-wide text-gray-400">
          {children}
        </span>
      </div>
    </div>
  )
}

export const authInputClasses =
  'block w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20'

export function AuthPrimaryButton({
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={clsx(
        'flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-blue-600',
        className,
      )}
    >
      {children}
    </button>
  )
}

export function GoogleButton({
  className,
  children = 'Continue with Google',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={clsx(
        'flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600',
        className,
      )}
    >
      <GoogleIcon className="h-5 w-5 shrink-0" />
      {children}
    </button>
  )
}

export function AuthLegalFooter() {
  return (
    <p className="mt-12 text-xs leading-5 text-gray-400">
      By continuing, you agree to our{' '}
      <a
        href="https://receiptsai.com/legal/terms-and-conditions"
        target="_blank"
        rel="noreferrer"
        className="font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
      >
        Terms
      </a>{' '}
      and{' '}
      <a
        href="https://receiptsai.com/legal/privacy-policy"
        target="_blank"
        rel="noreferrer"
        className="font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
      >
        Privacy Policy
      </a>
      .
    </p>
  )
}

function GoogleIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}
